/**
 * SeverityBadge — pill displaying a severity level. The `critical` level
 * pulses (1.6s ease-in-out scale 1↔1.3, opacity 1↔0.6); others sit still.
 * Pulse disabled under prefers-reduced-motion (handled in global.css `pulse`
 * keyframe — disabled when @media (prefers-reduced-motion: reduce) matches).
 *
 * Color map:
 *   critical → #ef4444 (red)    — pulses
 *   high     → #FFB000 (amber-warning)
 *   medium   → #D97757 (Claude amber / primary)
 *   low      → #6FA8FF (info blue)
 */

import { Box, Chip } from '@mui/material';
import type { ReactElement } from 'react';

import type { Severity } from '@/types/analysis';

export interface SeverityBadgeProps {
  severity: Severity;
  score?: number;
  size?: 'small' | 'medium';
}

const SEVERITY_CONFIG: Record<
  Severity,
  { color: string; bg: string; border: string; pulses: boolean; glow: boolean }
> = {
  critical: {
    color: '#ef4444',
    bg: 'rgba(239, 68, 68, 0.10)',
    border: 'rgba(239, 68, 68, 0.40)',
    pulses: true,
    glow: true,
  },
  high: {
    color: '#FFB000',
    bg: 'rgba(255, 176, 0, 0.10)',
    border: 'rgba(255, 176, 0, 0.40)',
    pulses: false,
    glow: false,
  },
  medium: {
    color: '#D97757',
    bg: 'rgba(217, 119, 87, 0.10)',
    border: 'rgba(217, 119, 87, 0.40)',
    pulses: false,
    glow: false,
  },
  low: {
    color: '#6FA8FF',
    bg: 'rgba(111, 168, 255, 0.10)',
    border: 'rgba(111, 168, 255, 0.40)',
    pulses: false,
    glow: false,
  },
};

export function SeverityBadge({
  severity,
  score,
  size = 'medium',
}: SeverityBadgeProps): ReactElement {
  const cfg = SEVERITY_CONFIG[severity];
  const label =
    score !== undefined ? `${severity.toUpperCase()} (${score})` : severity.toUpperCase();
  const ariaLabel = `severity: ${severity}${score !== undefined ? `, score ${score}` : ''}`;

  return (
    <Chip
      size={size}
      label={
        <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75 }}>
          <Box
            component="span"
            sx={{
              display: 'inline-block',
              width: 6,
              height: 6,
              borderRadius: '50%',
              backgroundColor: cfg.color,
              boxShadow: cfg.glow ? `0 0 4px ${cfg.color}` : 'none',
              animation: cfg.pulses ? 'pulse 1.6s ease-in-out infinite' : 'none',
              flex: 'none',
            }}
            aria-hidden="true"
          />
          {label}
        </Box>
      }
      aria-label={ariaLabel}
      sx={{
        backgroundColor: cfg.bg,
        color: cfg.color,
        border: '1px solid',
        borderColor: cfg.border,
        fontWeight: 600,
        fontSize: size === 'small' ? '0.6875rem' : '0.75rem',
        letterSpacing: '0.04em',
        textTransform: 'uppercase',
        height: size === 'small' ? 22 : 26,
        '.MuiChip-label': { px: 1, py: 0 },
      }}
    />
  );
}
